// @flow
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Card, CardActions, CardContent } from '@mui/material';
import MuiPhoneNumber from 'mui-phone-number';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import FormTextField from '../../common/components/FormTextField';
import PasswordTextField from '../../resetPassword/components/PasswordTextField';
import PrimaryButton from '../../common/components/PrimaryButton';
import ServerErrorAlert from '../../common/components/ServerErrorAlert';
import useNewAccountFormManager from './hooks/useNewAccountFormManager';
import LoadingDialog from './LoadingDialog';
import useScreenDimensions from '../../common/hooks/useScreenDimensions';
import TermsOfUseCheckbox from './TermsOfUseCheckbox';
import CoachSMSCheckbox from './CoachSMSCheckbox';
import TertiaryButton from '../../common/components/TertiaryButton';
import ExistingAccountDialog from './ExistingAccountDialog';
import FeatureFlag from '../../common/components/FeatureFlag';
import { FEATURES } from '../api/getPublicFeatures';
import usePublicFeatures from './hooks/usePublicFeatures';
import { navigate } from '../../state/navigation/navigation';

export default function CoachTrialForm() {
    const { width } = useScreenDimensions();
    const history = useHistory();

    const {
        name,
        setName,
        nameValidated,
        nameErrorMessage,
        email,
        setEmail,
        emailValidated,
        emailErrorMessage,
        password,
        setPassword,
        passwordValidated,
        passwordErrorMessage,
        hasAgreedToTerms,
        setHasAgreedToTerms,
        hasAgreedToSMS,
        setHasAgreedToSMS,
        showValidationErrors,
        isInProcess,
        isOpen,
        progress,
        createAccount,
        createAccountSuccessfulOrNotSubmitted,
        serverErrorMessages,
        setCountryCode,
        dialCode,
        setDialCode,
        phoneNumber,
        setPhoneNumber,
        phoneValidated,
        phoneErrorMessage,
        serverError,
        setServerError,
        HCAPTCHA_SITE_KEY,
        captchaRef,
        captchaRemountKey
    } = useNewAccountFormManager(true);

    usePublicFeatures();

    const [emailTouched, setEmailTouched] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [isExistingAccountDialogOpen, setIsExistingAccountDialogOpen] = useState(false);

    const hasPhoneNumber = phoneNumber.replace(/\D/g, '').length > dialCode.length;

    useEffect(() => {
        if (!hasPhoneNumber) {
            setHasAgreedToSMS(false);
        }
    }, [hasPhoneNumber, setHasAgreedToSMS]);

    const hasNameError = showValidationErrors && !nameValidated;
    const hasEmailError = !emailValidated && !emailFocused && (showValidationErrors || emailTouched);
    const hasPasswordError = showValidationErrors && !passwordValidated;
    const hasAgreedError = showValidationErrors && !hasAgreedToTerms;
    const hasPhoneError = showValidationErrors && !phoneValidated;

    const nameErrorText = createAccountSuccessfulOrNotSubmitted ? nameErrorMessage : serverErrorMessages.fullName;
    const emailErrorText = createAccountSuccessfulOrNotSubmitted ? emailErrorMessage : serverErrorMessages.email;
    const passwordErrorText = createAccountSuccessfulOrNotSubmitted
        ? passwordErrorMessage
        : serverErrorMessages.password;

    const shouldShowExistingAccountDialog =
        !createAccountSuccessfulOrNotSubmitted &&
        typeof serverErrorMessages.email === 'string' &&
        serverErrorMessages.email.toLowerCase().includes('already in use');

    useEffect(() => {
        if (!shouldShowExistingAccountDialog) {
            setIsExistingAccountDialogOpen(false);
            return undefined;
        }

        const frameId = requestAnimationFrame(() => {
            setIsExistingAccountDialogOpen(true);
        });

        return () => cancelAnimationFrame(frameId);
    }, [shouldShowExistingAccountDialog]);

    const goToConvertPage = (emailToPrefill: string) => {
        setIsExistingAccountDialogOpen(false);
        history.push('/create-account/convert', {
            email: emailToPrefill || email,
        });
    };

    const handleCreateAccountViaEnterKey = event => {
        const enterKeyPressed = event.key === 'Enter' || event.keyCode === 13 || event.which === 13;

        if (enterKeyPressed) {
            createAccount();
        }
    };

    const displayCoachQuestions = () => (
        <>
            <div className="form-input">
                <MuiPhoneNumber
                    onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                    data-testid="text-field-input"
                    className="form-textfield"
                    inputProps={{
                        name: 'phone'
                    }}
                    InputLabelProps={{
                        classes: {
                            root: 'input',
                            asterisk: 'label-asterisk',
                            focused: 'input-focus'
                        }
                    }}
                    type="tel"
                    size="small"
                    variant="standard"
                    label="Phone Number (optional)"
                    value={phoneNumber}
                    defaultCountry="us"
                    disableAreaCodes={true}
                    error={hasPhoneError}
                    helperText={
                        hasPhoneError
                            ? phoneErrorMessage
                            : 'Please include your mobile number for the best onboarding support.'
                    }
                    FormHelperTextProps={
                        hasPhoneError
                            ? undefined
                            : { className: 'phone-helper-text' }
                    }
                    onChange={(value, country) => {
                        setCountryCode(country.countryCode.toUpperCase());
                        setDialCode(country.dialCode);
                        setPhoneNumber(value);
                    }}
                />
            </div>
        </>
    );

    return (
        <div className="container payment-container">
            <div>
                <Card data-testid="card" classes={{ root: 'form-card card' }}>
                    <div className="title-container">
                        <div className="title">
                            Start your free
                            <br /> 14-day coach trial
                        </div>
                        {width > 375 ? (
                            <img
                                alt="highlight"
                                className="title-highlight"
                                height={50}
                                width={350}
                                src="https://static.trainheroic.com/icons/Highlight.Long2.png"
                            />
                        ) : (
                            <div className="title-placeholder" />
                        )}
                    </div>
                    <form className="form-padding padding-desktop">
                        <div className="form-input">
                            <FormTextField
                                onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                required
                                type="text"
                                label="First and Last Name"
                                value={name}
                                variant="standard"
                                onChange={event => setName(event.target.value)}
                                error={hasNameError}
                                helperText={hasNameError ? nameErrorText : ''}
                            />
                        </div>
                        <div className="form-input">
                            <FormTextField
                                onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                required
                                type="email"
                                label="Email"
                                value={email}
                                variant="standard"
                                onChange={event => setEmail(event.target.value)}
                                onFocus={() => setEmailFocused(true)}
                                onBlur={() => {
                                    setEmailFocused(false);
                                    setEmailTouched(true);
                                }}
                                error={hasEmailError}
                                helperText={hasEmailError ? emailErrorText : ''}
                            />
                        </div>
                        <div className="form-input">
                            <PasswordTextField
                                onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                required
                                label="Password"
                                value={password}
                                onChange={value => setPassword(value)}
                                hasError={hasPasswordError}
                                helperText={hasPasswordError ? passwordErrorText : ''}
                            />
                        </div>
                        {displayCoachQuestions()}
                    </form>
                    <CardContent classes={{ root: 'padding-desktop' }}>
                        <FeatureFlag feature={FEATURES.WEB_COACH_SMS_CHECKBOX_V1}>
                            <CoachSMSCheckbox
                                hasAgreed={hasAgreedToSMS}
                                onChange={() => setHasAgreedToSMS(!hasAgreedToSMS)}
                                disabled={!hasPhoneNumber}
                            />
                        </FeatureFlag>
                        <TermsOfUseCheckbox
                            hasAgreed={hasAgreedToTerms}
                            hasAgreedError={hasAgreedError}
                            onChange={() => setHasAgreedToTerms(!hasAgreedToTerms)}
                        />
                    </CardContent>
                    <CardActions classes={{ root: 'action-container' }}>
                        <HCaptcha
                            key={captchaRemountKey}
                            ref={captchaRef}
                            sitekey={HCAPTCHA_SITE_KEY}
                            size="invisible"
                            onVerify={() => {}}
                        />
                        <div className="action-buttons-container">
                            <PrimaryButton
                                showActivity={isInProcess}
                                disabled={isInProcess}
                                onClick={() => createAccount()}
                            >
                                Start Your Free Trial
                            </PrimaryButton>
                            <div className="cta-reassurance">No credit card required &bull; Cancel anytime</div>
                        </div>
                    </CardActions>
                </Card>
                <div className="action-buttons-container button-margin">
                    <TertiaryButton className="coach-trial-link" onClick={() => navigate('/login')}>
                        Already Have an Account? Login.
                    </TertiaryButton>
                    <TertiaryButton className="coach-trial-link" onClick={() => goToConvertPage()}>
                        Already Have an Athlete Account? Convert to Coach.
                    </TertiaryButton>
                </div>
                <LoadingDialog isOpen={isOpen} progress={progress} />
            </div>
            <ServerErrorAlert
                title="There was an error creating your account."
                open={serverError}
                handleClose={() => setServerError(false)}
            />
            <ExistingAccountDialog
                isOpen={isExistingAccountDialogOpen}
                onClose={() => setIsExistingAccountDialogOpen(false)}
                onProceed={() => goToConvertPage(email)}
            />
        </div>
    );
}
